import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../../components/PublicLayout';
import { Building, ArrowLeft, Mail } from 'lucide-react';

const SimulationCopropriete: React.FC = () => {
    const navigate = useNavigate();

    const PRIX_ETUDE = 800;
    const PRIX_INFRA_MIN = 2000;
    const PRIX_INFRA_MAX = 5000;
    const PRIX_PAR_BORNE = 1200;
    const PRIX_SUPERVISION_PAR_BORNE = 150;
    const PLAFOND_ADVENIR_PAR_BORNE = 1660;

    const [formState, setFormState] = useState(() => {
        const savedData = localStorage.getItem('coproFormData');
        if (savedData) {
            return JSON.parse(savedData);
        } else {
            return {
                nbPlaces: 10,
                nbBornes: 2,
                solution: 'collectif',
                puissance: 7.4,
                gestion: 'statique',
                supervision: false,
                rfid: false,
            };
        }
    });

    const calculation = useMemo(() => {
        let etudeCost = PRIX_ETUDE;
        let infraCost = formState.solution === 'collectif' ? PRIX_INFRA_MIN + (formState.nbPlaces * 50) : 0;
        if (infraCost > PRIX_INFRA_MAX) infraCost = PRIX_INFRA_MAX;

        let bornesCost = formState.nbBornes * PRIX_PAR_BORNE;
        let supervisionCost = formState.supervision ? formState.nbBornes * PRIX_SUPERVISION_PAR_BORNE : 0;

        let totalBrut = etudeCost + infraCost + bornesCost + supervisionCost;

        let primeAdvenir = (infraCost + bornesCost) * 0.5;
        let plafondPrime = formState.nbBornes * PLAFOND_ADVENIR_PAR_BORNE;
        if (primeAdvenir > plafondPrime) {
            primeAdvenir = plafondPrime;
        }

        let totalNet = totalBrut - primeAdvenir;

        return { etudeCost, infraCost, bornesCost, supervisionCost, totalBrut, primeAdvenir, totalNet };

    }, [formState]);

    useEffect(() => {
        localStorage.setItem('coproFormData', JSON.stringify(formState));
    }, [formState]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            const { checked } = e.target as HTMLInputElement;
            setFormState(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormState(prev => ({ ...prev, [name]: value }));
        }
    };

    const sendMail = () => {
        const subject = "Demande de devis via le simulateur - Copropriété";
        const body = `
Bonjour,

Voici le résumé d'une simulation effectuée sur le site ELECBORNE pour une copropriété :

ESTIMATION TOTALE : ${calculation.totalNet.toFixed(2)} €

DÉTAILS DU PROJET :
---------------------------------
Nombre de places de parking : ${formState.nbPlaces}
Nombre de bornes (Phase 1) : ${formState.nbBornes}
Type de solution : ${formState.solution}
Puissance par borne : ${formState.puissance} kW
Gestion de la charge : ${formState.gestion}

OPTIONS :
---------------------------------
Système de supervision : ${formState.supervision ? 'Oui' : 'Non'}
Badges RFID : ${formState.rfid ? 'Oui' : 'Non'}

Merci de bien vouloir nous contacter pour une étude approfondie.

Cordialement,
[Le Syndic / Le Conseil Syndical]
                `.trim().replace(/^ +/gm, '');

        window.location.href = `mailto:contact@elecborne.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    return (
        <PublicLayout>
            <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
                <button onClick={() => navigate('/simulateur')} className="flex items-center text-gray-600 hover:text-primary-600 mb-6">
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Retour aux simulateurs
                </button>
                <div className="bg-white p-8 rounded-2xl shadow-lg">
                    <div className="text-center mb-8">
                        <Building className="mx-auto w-12 h-12 text-primary-600" />
                        <h1 className="text-3xl font-bold text-gray-900 mt-4">Simulateur IRVE - Copropriété</h1>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12">
                        {/* Formulaire */}
                        <div className="space-y-6">
                            <div>
                                <label className="font-semibold">Nombre de places de parking</label>
                                <input type="number" name="nbPlaces" value={formState.nbPlaces} onChange={handleInputChange} className="mt-2 w-full p-3 border rounded-lg" min="1" />
                            </div>
                            <div>
                                <label className="font-semibold">Nombre de bornes (Phase 1)</label>
                                <input type="number" name="nbBornes" value={formState.nbBornes} onChange={handleInputChange} className="mt-2 w-full p-3 border rounded-lg" min="1" />
                            </div>
                            <div>
                                <label className="font-semibold">Type de solution</label>
                                <select name="solution" value={formState.solution} onChange={handleInputChange} className="mt-2 w-full p-3 border rounded-lg">
                                    <option value="individuel">Individuel (droit à la prise)</option>
                                    <option value="collectif">Collectif</option>
                                </select>
                            </div>
                            <div>
                                <label className="font-semibold">Puissance par borne</label>
                                <select name="puissance" value={formState.puissance} onChange={handleInputChange} className="mt-2 w-full p-3 border rounded-lg">
                                    <option value="3.7">3.7kW</option>
                                    <option value="7.4">7.4kW</option>
                                    <option value="11">11kW</option>
                                    <option value="22">22kW</option>
                                </select>
                            </div>
                            <div>
                                <label className="font-semibold">Gestion de la charge</label>
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    <button onClick={() => setFormState(prev => ({...prev, gestion: 'statique'}))} className={`p-3 rounded-lg text-center ${formState.gestion === 'statique' ? 'bg-primary-600 text-white' : 'bg-gray-100'}`}>Statique</button>
                                    <button onClick={() => setFormState(prev => ({...prev, gestion: 'dynamique'}))} className={`p-3 rounded-lg text-center ${formState.gestion === 'dynamique' ? 'bg-primary-600 text-white' : 'bg-gray-100'}`}>Dynamique</button>
                                </div>
                            </div>
                            <div>
                                <label className="font-semibold">Système de facturation</label>
                                <div className="mt-2 space-y-2">
                                    <div className="flex items-center"><input type="checkbox" name="supervision" checked={formState.supervision} onChange={handleInputChange} className="h-5 w-5 rounded"/><label className="ml-3">Supervision & Facturation auto</label></div>
                                    <div className="flex items-center"><input type="checkbox" name="rfid" checked={formState.rfid} onChange={handleInputChange} className="h-5 w-5 rounded"/><label className="ml-3">Badges RFID</label></div>
                                </div>
                            </div>
                        </div>

                        {/* Resultat */}
                        <div className="bg-green-50 p-6 rounded-xl border-l-4 border-green-500">
                            <h2 className="text-2xl font-bold text-green-800 mb-4">Votre Estimation</h2>
                            <div className="space-y-3 text-gray-700">
                                <div className="flex justify-between"><span>Étude technique</span><span>{calculation.etudeCost.toFixed(2)} €</span></div>
                                {calculation.infraCost > 0 && <div className="flex justify-between"><span>Infrastructure collective</span><span>{calculation.infraCost.toFixed(2)} €</span></div>}
                                <div className="flex justify-between"><span>Bornes</span><span>{calculation.bornesCost.toFixed(2)} €</span></div>
                                {calculation.supervisionCost > 0 && <div className="flex justify-between"><span>Supervision</span><span>{calculation.supervisionCost.toFixed(2)} €</span></div>}
                                <hr className="my-3"/>
                                <div className="flex justify-between font-bold"><span>Sous-total</span><span>{calculation.totalBrut.toFixed(2)} €</span></div>
                                <div className="flex justify-between"><span>Prime Advenir</span><span className="text-green-600">- {calculation.primeAdvenir.toFixed(2)} €</span></div>
                                <hr className="my-3"/>
                                <p className="text-center text-lg font-semibold">TOTAL NET ESTIMÉ</p>
                                <p className="text-center text-4xl font-bold text-green-700">{calculation.totalNet.toFixed(2)} €</p>
                            </div>
                             <button onClick={sendMail} className="mt-6 w-full bg-primary-600 text-white font-bold py-3 rounded-lg flex items-center justify-center hover:bg-primary-700">
                                <Mail className="w-5 h-5 mr-2" /> Envoyer mon devis
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </PublicLayout>
    );
};

export default SimulationCopropriete;
